import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useLanguage } from '../context/LanguageContext';
import { partnershipAPI } from '../services/api';
import { toast } from 'react-toastify';
import SEO from '../components/SEO';
import { validateForm } from '../utils/validation';

const Partnership = () => {
  const { t } = useLanguage();
  const [formData, setFormData] = useState({
    companyName: '',
    contactPerson: '',
    email: '',
    phone: '',
    website: '',
    brandDescription: '',
    cooperationDetails: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);
  const [errors, setErrors] = useState({});

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
    // Clear error for this field when user starts editing
    if (errors[e.target.name]) {
      setErrors({
        ...errors,
        [e.target.name]: null,
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Define validation rules for partnership form
    const validationRules = {
      companyName: { required: true, minLength: 2 },
      contactPerson: { required: true, minLength: 2 },
      email: { required: true, email: true },
      phone: { required: true, phone: true },
      website: { url: true },
      brandDescription: { required: true, minLength: 10 },
      cooperationDetails: { required: true, minLength: 10 },
    };

    // Validate form
    const validationErrors = validateForm(formData, validationRules, t);
    
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      toast.error(t('contact.validationError'), { autoClose: 2000 });
      return;
    }

    setIsSubmitting(true);
    
    try {
      const response = await partnershipAPI.apply(formData);
      
      setSubmitStatus('success');
      toast.success(t('toast.partnershipSubmitted'), { autoClose: 1000 });
      
      // Clear form
      setFormData({
        companyName: '',
        contactPerson: '',
        email: '',
        phone: '',
        website: '',
        brandDescription: '',
        cooperationDetails: '',
      });
      setErrors({});
      
      setTimeout(() => setSubmitStatus(null), 5000);
    } catch (error) {
      setSubmitStatus('error');
      toast.error(error.response?.data?.detail || t('toast.partnershipFailed'), { autoClose: 1000 });
      setTimeout(() => setSubmitStatus(null), 5000);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <SEO
        title={t('brands.partnershipFormTitle')}
        description="Apply to become a certified Meller Racing tuning point. Partner with us to offer premium motorcycle tuning services."
        keywords="partnership, certified tuning point, motorcycle tuning partner, business cooperation"
        type="website"
      />

      {/* Hero Section */}
      <section className="relative bg-racing-black text-white py-20">
        <div className="absolute inset-0 bg-gradient-to-b from-transparent to-racing-black opacity-50"></div>
        <div className="container-custom relative">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center"
          >
            <h1 className="text-4xl md:text-5xl font-display font-bold mb-4">
              {t('brands.ctaTitle')}
            </h1>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              {t('brands.ctaDescription')}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Partnership Application Form */}
      <section className="py-16 bg-gray-50">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-3xl mx-auto"
          >
            <div className="bg-white rounded-lg shadow-lg p-8">
              <h2 className="text-3xl font-display font-bold text-racing-black mb-6">
                {t('brands.partnershipFormTitle')}
              </h2>

              {submitStatus === 'success' && (
                <div className="mb-6 p-4 bg-green-100 border border-green-400 text-green-700 rounded-lg">
                  {t('brands.partnershipSuccessMessage')}
                </div>
              )}

              {submitStatus === 'error' && (
                <div className="mb-6 p-4 bg-red-100 border border-red-400 text-red-700 rounded-lg">
                  {t('brands.partnershipErrorMessage')}
                </div>
              )}

              <form onSubmit={handleSubmit} noValidate className="space-y-5">
                <div className="grid md:grid-cols-2 gap-5">
                  <div>
                    <label htmlFor="companyName" className="block text-sm font-medium text-gray-700 mb-2">
                      {t('brands.companyName')} *
                    </label>
                    <input
                      type="text"
                      id="companyName"
                      name="companyName"
                      value={formData.companyName}
                      onChange={handleChange}
                      required
                      className={`w-full px-4 py-3 bg-white border rounded-lg focus:ring-2 focus:ring-racing-red focus:border-racing-red transition ${
                        errors.companyName ? 'border-red-500' : 'border-gray-300'
                      }`}
                      placeholder={t('brands.companyNamePlaceholder')}
                    />
                    {errors.companyName && (
                      <p className="text-red-500 text-sm mt-2">{errors.companyName}</p>
                    )}
                  </div>

                  <div>
                    <label htmlFor="contactPerson" className="block text-sm font-medium text-gray-700 mb-2">
                      {t('brands.contactPerson')} *
                    </label>
                    <input
                      type="text"
                      id="contactPerson"
                      name="contactPerson"
                      value={formData.contactPerson}
                      onChange={handleChange}
                      required
                      className={`w-full px-4 py-3 bg-white border rounded-lg focus:ring-2 focus:ring-racing-red focus:border-racing-red transition ${
                        errors.contactPerson ? 'border-red-500' : 'border-gray-300'
                      }`}
                      placeholder={t('brands.contactPersonPlaceholder')}
                    />
                    {errors.contactPerson && (
                      <p className="text-red-500 text-sm mt-2">{errors.contactPerson}</p>
                    )}
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-5">
                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                      {t('brands.email')} *
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      className={`w-full px-4 py-3 bg-white border rounded-lg focus:ring-2 focus:ring-racing-red focus:border-racing-red transition ${
                        errors.email ? 'border-red-500' : 'border-gray-300'
                      }`}
                      placeholder={t('brands.emailPlaceholder')}
                    />
                    {errors.email && (
                      <p className="text-red-500 text-sm mt-2">{errors.email}</p>
                    )}
                  </div>

                  <div>
                    <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2">
                      {t('brands.phone')} *
                    </label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      required
                      className={`w-full px-4 py-3 bg-white border rounded-lg focus:ring-2 focus:ring-racing-red focus:border-racing-red transition ${
                        errors.phone ? 'border-red-500' : 'border-gray-300'
                      }`}
                      placeholder={t('brands.phonePlaceholder')}
                    />
                    {errors.phone && (
                      <p className="text-red-500 text-sm mt-2">{errors.phone}</p>
                    )}
                  </div>
                </div>

                <div>
                  <label htmlFor="website" className="block text-sm font-medium text-gray-700 mb-2">
                    {t('brands.website')}
                  </label>
                  <input
                    type="text"
                    id="website"
                    name="website"
                    value={formData.website}
                    onChange={handleChange}
                    className={`w-full px-4 py-3 bg-white border rounded-lg focus:ring-2 focus:ring-racing-red focus:border-racing-red transition ${
                      errors.website ? 'border-red-500' : 'border-gray-300'
                    }`}
                    placeholder={t('brands.websitePlaceholder')}
                  />
                  {errors.website && (
                    <p className="text-red-500 text-sm mt-2">{errors.website}</p>
                  )}
                </div>

                <div>
                  <label htmlFor="brandDescription" className="block text-sm font-medium text-gray-700 mb-2">
                    {t('brands.brandDescription')} *
                  </label>
                  <textarea
                    id="brandDescription"
                    name="brandDescription"
                    value={formData.brandDescription}
                    onChange={handleChange}
                    required
                    rows="4"
                    className={`w-full px-4 py-3 bg-white border rounded-lg focus:ring-2 focus:ring-racing-red focus:border-racing-red transition resize-none ${
                      errors.brandDescription ? 'border-red-500' : 'border-gray-300'
                    }`}
                    placeholder={t('brands.brandDescriptionPlaceholder')}
                  ></textarea>
                  {errors.brandDescription && (
                    <p className="text-red-500 text-sm mt-2">{errors.brandDescription}</p>
                  )}
                </div>

                <div>
                  <label htmlFor="cooperationDetails" className="block text-sm font-medium text-gray-700 mb-2">
                    {t('brands.cooperationDetails')} *
                  </label>
                  <textarea
                    id="cooperationDetails"
                    name="cooperationDetails"
                    value={formData.cooperationDetails}
                    onChange={handleChange}
                    required
                    rows="5"
                    className={`w-full px-4 py-3 bg-white border rounded-lg focus:ring-2 focus:ring-racing-red focus:border-racing-red transition resize-none ${
                      errors.cooperationDetails ? 'border-red-500' : 'border-gray-300'
                    }`}
                    placeholder={t('brands.cooperationDetailsPlaceholder')}
                  ></textarea>
                  {errors.cooperationDetails && (
                    <p className="text-red-500 text-sm mt-2">{errors.cooperationDetails}</p>
                  )}
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full py-4 bg-racing-red hover:bg-racing-darkRed text-white font-bold rounded-lg transition disabled:opacity-50 disabled:cursor-not-allowed shadow-lg text-lg"
                >
                  {isSubmitting ? t('brands.sending') : t('brands.submitPartnership')}
                </button>
              </form>
            </div>
          </motion.div>
        </div>
      </section>
    </>
  );
};

export default Partnership;
